import React from 'react';
import type { SyncFeed } from '../../../../api/stays-sync-client';
import { AppButton } from '../../../button/app-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import { feedPill, feedTitle } from '../../../../utils/stays-sync';
import { Actions, RowItem, RowLine, RowMain, RowTitle } from './sync.styled';

/**
 * ONE CONNECTED CALENDAR (plan S5, screens §1 "Row per feed"): the provider
 * by name (never a logo), its label, the link's hint (host + last 4 — the
 * link itself is never sent to the browser), the health pill, and — for a
 * host with stays:manage only — Sync now, Settings and Remove. Every control's
 * accessible name names the calendar, so two rows never share one.
 */

export interface FeedRowProps {
  feed: SyncFeed;
  /** The reader's clock (R-T12) for the pill's time. */
  zone: string;
  now: Date;
  canManage: boolean;
  syncing: boolean;
  /** The visible reason "Sync now" is unavailable (a refused sync's wait), else null. */
  waitReason: string | null;
  onSync: (feed: SyncFeed) => void;
  onSettings: (feed: SyncFeed) => void;
  onRemove: (feed: SyncFeed) => void;
}

export const FeedRow: React.FC<FeedRowProps> = ({
  feed,
  zone,
  now,
  canManage,
  syncing,
  waitReason,
  onSync,
  onSettings,
  onRemove,
}) => {
  const title = feedTitle(feed);
  const pill = feedPill(feed, zone, now, syncing);
  // A disabled control says why, visibly (UI-CONTRACT §1).
  const reason = waitReason || (feed.paused ? 'This calendar is paused. Turn it back on in Settings to sync it.' : null);

  return (
    <RowItem data-testid={`sync-feed-${feed.id}`} data-provider={feed.provider}>
      <RowMain>
        <RowTitle>{feed.provider === 'other' ? title : feed.provider_name}</RowTitle>
        <RowLine>
          {feed.label && feed.provider !== 'other' ? <span>{feed.label}</span> : null}
          <span data-testid="sync-feed-hint">{feed.url_hint}</span>
        </RowLine>
        <RowLine>
          <StatusPill tone={pill.tone} dot data-at={pill.at || undefined} data-testid="sync-feed-pill" role="status">
            {pill.text}
          </StatusPill>
          {feed.auto_approve ? <span>New stays are added without asking</span> : null}
        </RowLine>
      </RowMain>
      {canManage ? (
        <Actions>
          <ActionWithReason reason={reason}>
            {describedBy => (
              <AppButton
                variant="secondary"
                size="compact"
                type="button"
                loading={syncing}
                loadingLabel="Syncing…"
                disabled={!!reason}
                aria-describedby={describedBy}
                aria-label={`Sync the ${title} now`}
                onClick={() => onSync(feed)}
              >
                Sync now
              </AppButton>
            )}
          </ActionWithReason>
          <AppButton variant="secondary" size="compact" type="button" aria-label={`${title} settings`} onClick={() => onSettings(feed)}>
            Settings
          </AppButton>
          <AppButton variant="secondary" size="compact" type="button" aria-label={`Remove the ${title}`} onClick={() => onRemove(feed)}>
            Remove
          </AppButton>
        </Actions>
      ) : null}
    </RowItem>
  );
};
